import { OWNER_KIND } from "../transactions/index.ts";
import { MANUAL_CHAIN_TUNING } from "./config.ts";
import { cargoKind, countOwnedItems } from "./state.ts";
import type { ManualChainPresentationSnapshot, ManualChainSnapshot } from "./types.ts";

export function projectManualChainPresentation(
  snapshot: ManualChainSnapshot,
  reducedMotion = false,
): Readonly<ManualChainPresentationSnapshot> {
  const state = snapshot.state;
  const active = state.activeAction;
  const eggIds = state.ledger.items
    .filter((item) => item.kind === 2)
    .map((item) => item.itemId)
    .sort((left, right) => left - right);
  const collectedEggIds = state.ledger.items
    .filter((item) => item.kind === 2 && item.ownerKind === OWNER_KIND.ACTOR_CARGO)
    .map((item) => item.itemId)
    .sort((left, right) => left - right);
  const sale = [...state.events].reverse().find((event) => event.kind === "sale-committed") ?? null;
  return Object.freeze({
    tick: state.tick,
    gameplayHash: snapshot.gameplayHash,
    player: Object.freeze({
      position: Object.freeze({ x: state.player.xMicroWU / 1_000_000, z: state.player.zMicroWU / 1_000_000 }),
      facingRadians: Math.atan2(state.player.facingXMicroWU, state.player.facingZMicroWU),
      moving: state.player.moving,
    }),
    plots: Object.freeze(state.plots.map((plot) => Object.freeze({
      id: plot.id,
      state: plot.state,
      cooldown01: plot.state === "ready" ? 0 : plot.cooldownTicks / MANUAL_CHAIN_TUNING.plotCooldownTicks,
    }))),
    cargo: Object.freeze({
      kind: cargoKind(state),
      count: countOwnedItems(state, OWNER_KIND.ACTOR_CARGO, 1),
      capacity: 5 as const,
    }),
    pen: Object.freeze({
      stagedCorn: countOwnedItems(state, OWNER_KIND.PEN_STAGING, 2, 1),
      tending: active?.verb === "tend",
      tend01: active?.verb === "tend" ? Math.min(1, active.progressTicks / active.requiredTicks) : 0,
    }),
    interaction: active === null ? null : Object.freeze({
      kind: active.verb,
      progress01: Math.min(1, active.progressTicks / active.requiredTicks),
    }),
    nest: Object.freeze({ createdEggIds: Object.freeze(eggIds), collectedEggIds: Object.freeze(collectedEggIds) }),
    market: Object.freeze({ salePulseId: sale?.id ?? null }),
    cash: state.ledger.currency,
    pad: Object.freeze({ state: state.penExtensionAvailable ? "active" as const : "locked" as const, price: 24 as const }),
    objectiveId: state.objectiveId,
    reducedMotion,
    events: state.events,
  });
}
